import { useMemo } from 'react';
import intl from 'react-intl-universal';
import { useSelector } from 'react-redux';
import { Datepicker, FormItem, Typography } from '@getsynapse/design-system';
import { DashboardFilters } from 'utils/types/dashboardFilters';
import MultiSelectDropdown from 'Organisms/MultiSelectDropdow/MultiSelectDropdown';
import { Option } from 'utils/customTypes';
import { insightFilterOptionsSelectors } from 'state/normalized/insightFilterOptions/insightFilterOptionsSlice';

const dateToString = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const stringToDate = (date: string | undefined): Date | null => {
  if (!date || date.split('-').length !== 3) return null;
  return new Date(date.split('-').join('/'));
};

type GeneralFilters = Pick<
  DashboardFilters,
  'startDate' | 'endDate' | 'learningTeams'
>;

type GeneralDashboardFiltersProps = {
  filters: DashboardFilters;
  onFilterChange: (filter: GeneralFilters) => void;
  canFilter?: boolean;
};

const GeneralDashboardFilters = ({
  filters,
  onFilterChange,
  canFilter = true,
}: GeneralDashboardFiltersProps) => {
  const learningTeamsOptions = useSelector(
    insightFilterOptionsSelectors.selectLearningTeamsOptions
  );
  const handleDateChange = (date: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    onFilterChange({
      ...filters,
      startDate: dateToString(date.startDate),
      endDate: dateToString(date.endDate),
    });
  };

  const handleTeamChange = (options: Option[]) => {
    if (options.length > 0) {
      onFilterChange({
        ...filters,
        learningTeams: options.map((option) => option.value),
      });
      return;
    }

    const newFilters = { ...filters };
    delete newFilters?.learningTeams;

    onFilterChange({
      ...newFilters,
    });
  };

  const selectedTeamValues: Option[] = useMemo(() => {
    if (!filters.learningTeams) return [];

    return filters.learningTeams?.map((teamId) => {
      const team = learningTeamsOptions.find(
        (option) => option.value === teamId
      );
      return { label: team?.label || teamId, value: teamId };
    });
  }, [filters.learningTeams, learningTeamsOptions]);

  return (
    <div className='flex flex-col space-y-4'>
      <Typography variant='h5'>{intl.get('GENERAL')}</Typography>

      <FormItem label={intl.get('FILTER_GENERAL.TIMEFRAME')}>
        <div className='w-full flex space-x-4'>
          <Datepicker
            className='w-full'
            startDate={stringToDate(filters.startDate)}
            endDate={stringToDate(filters.endDate)}
            startPlaceHolder={intl.get('FILTER_GENERAL.FROM')}
            endPlaceHolder={intl.get('FILTER_GENERAL.TO')}
            canSelectRange
            onPickDate={handleDateChange}
          />
        </div>
      </FormItem>

      <FormItem label={intl.get('FILTER_GENERAL.TEAMS')}>
        <MultiSelectDropdown
          options={learningTeamsOptions}
          values={selectedTeamValues}
          onChange={handleTeamChange}
          placeholder={intl.get('FILTER_GENERAL.TEAMS_PLACEHOLDER')}
          disabled={!canFilter}
          triggerProps={{
            'data-testid': 'ld-teams-filter-dropdown',
          }}
        />
      </FormItem>
    </div>
  );
};

export default GeneralDashboardFilters;
